import type { CSSProperties } from 'react';
import { useState } from 'react';

import type { AssignmentMode, MapVenue, RegionNode } from '../../../../api/regions';

interface Props {
  venue: MapVenue;
  regions: RegionNode[];
  pending: boolean;
  onApply: (mode: AssignmentMode, regionId?: string | null) => void;
}

const OPTIONS: ReadonlyArray<{ mode: AssignmentMode; title: string; desc: string }> = [
  {
    mode: 'auto',
    title: 'Auto-assign by location',
    desc: 'Resolve region from the venue’s coordinates.',
  },
  {
    mode: 'pin',
    title: 'Pin to a region',
    desc: 'Force into a chosen region regardless of coordinates.',
  },
  {
    mode: 'exclude',
    title: 'Exclude from auto-assign',
    desc: 'Keep out of automatic assignment (e.g. pop-up).',
  },
];

/** Flatten the region tree to indented <option>s. */
function regionOptions(regions: RegionNode[]): Array<{ id: string; label: string }> {
  const out: Array<{ id: string; label: string }> = [];
  const walk = (n: RegionNode, depth: number) => {
    out.push({ id: n.id, label: `${' '.repeat(depth)}${n.name}` });
    n.children.forEach((c) => walk(c, depth + 1));
  };
  regions.forEach((r) => walk(r, 0));
  return out;
}

const optionStyle = (on: boolean, tone: string): CSSProperties => ({
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '10px 12px',
  borderRadius: 10,
  cursor: 'pointer',
  background: on ? 'var(--nb-primary-soft)' : 'var(--nb-bg)',
  border: `1.5px solid ${on ? tone : 'var(--nb-border)'}`,
  marginBottom: 8,
});

const TONE: Record<AssignmentMode, string> = {
  auto: 'var(--nb-primary)',
  pin: '#0e874b',
  exclude: '#dc2626',
};

/** Auto / pin / exclude control for the selected venue. */
export function VenueExceptionControl({ venue, regions, pending, onApply }: Props) {
  const [pinRegion, setPinRegion] = useState(venue.regionId ?? '');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--nb-ink)' }}>{venue.name}</span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: 'var(--nb-subtle)' }}>
          {venue.regionName ?? 'Unassigned'}
        </span>
      </div>
      {OPTIONS.map((o) => {
        const on = venue.exception === o.mode;
        return (
          <button
            key={o.mode}
            disabled={pending}
            onClick={() => onApply(o.mode, o.mode === 'pin' ? pinRegion || null : null)}
            style={optionStyle(on, TONE[o.mode])}
          >
            <div
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: on ? TONE[o.mode] : 'var(--nb-ink)',
              }}
            >
              {o.title}
            </div>
            <div style={{ fontSize: 11, color: 'var(--nb-muted)' }}>{o.desc}</div>
          </button>
        );
      })}
      <select
        aria-label="Pin region"
        value={pinRegion}
        onChange={(e) => setPinRegion(e.target.value)}
        style={{
          width: '100%',
          height: 32,
          borderRadius: 8,
          border: '1px solid var(--nb-border)',
          background: 'var(--nb-bg)',
          font: 'inherit',
          fontSize: 12.5,
          color: 'var(--nb-ink)',
          padding: '0 8px',
        }}
      >
        <option value="">Choose a region to pin…</option>
        {regionOptions(regions).map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
